import type { MedplumClient } from "@medplum/core";
import type { ChargeItem, Provenance, Reference } from "@medplum/fhirtypes";

import { setAccountsBestEffort } from "@/utils/accounts";
import { processWithConcurrencyLimit } from "@/utils/concurrency";
import { joinReferenceString } from "@/utils/medplum";
import { waitForRateLimitRenewalIfNeeded } from "@/utils/rate-limit";
import { captureExceptionWithoutPhi } from "@/utils/sentry";

import { ChargeItemProvenanceActivityCode } from "../constants/charge-item-provenance";
import {
  buildChargeItemProvenance,
  practiceOrganizationIdFromChargeItem,
} from "../utils/charge-item-provenance";

import {
  recordVersionedTargetProvenance,
  type RecordVersionedTargetProvenanceResult,
} from "./record-versioned-target-provenance";

export type RecordChargeItemProvenanceResult = RecordVersionedTargetProvenanceResult;

/**
 * Records who changed a service and when, as a Provenance targeting the written ChargeItem version.
 *
 * @param medplum The Medplum client.
 * @param chargeItem The ChargeItem as returned by the write, carrying the version to stamp.
 * @param activityCode The mutation being recorded.
 * @param practiceOrganizationId Practice to account the Provenance to when the ChargeItem has none.
 * @returns The created Provenance, or the reason it could not be written.
 */
export async function recordChargeItemProvenance(
  medplum: MedplumClient,
  chargeItem: ChargeItem,
  activityCode: ChargeItemProvenanceActivityCode,
  practiceOrganizationId?: string,
): Promise<RecordChargeItemProvenanceResult> {
  const result = await recordVersionedTargetProvenance(
    medplum,
    (agentReference) =>
      buildChargeItemProvenance({
        chargeItem,
        activityCode,
        agentReference,
        practiceOrganizationId,
      }),
    {
      noProfileMessage: "Service mutation recorded no Provenance: no active profile",
      extra: { chargeItemId: chargeItem.id, activityCode },
    },
  );

  // The ChargeItem is already saved. A failed $set-accounts is a history gap, not a failed write.
  if (result.ok) {
    try {
      await linkToChargeItemPractice(medplum, result.data, chargeItem, practiceOrganizationId);
    } catch (error) {
      captureExceptionWithoutPhi(
        "ChargeItem Provenance filing failed",
        error,
        { provenanceId: result.data.id, activityCode },
        "warning",
      );
    }
  }

  return result;
}

/**
 * Files the Provenance under the ChargeItem's practice.
 *
 * Create ignores `meta.accounts` for a non-admin writer. The server then assigns the compartment
 * from the writer's membership, which can be the wrong practice when the user belongs to several.
 * `$set-accounts` sets the practice on the saved resource. It only allows practices the user has
 * a `PractitionerRole` for.
 *
 * `$qh-set-accounts` runs a bot, so it is skipped when the create already filed the record under
 * that practice alone — the usual case for a user in one practice.
 *
 * Failure here does not fail the ChargeItem write. Sentry reports the gap.
 */
async function linkToChargeItemPractice(
  medplum: MedplumClient,
  provenance: Provenance,
  chargeItem: ChargeItem,
  practiceOrganizationId?: string,
): Promise<void> {
  const organizationId = practiceOrganizationIdFromChargeItem(chargeItem, practiceOrganizationId);
  if (!organizationId) return;

  const organizationReference = joinReferenceString("Organization", organizationId);
  if (isFiledOnlyUnder(provenance, organizationReference)) return;

  await setAccountsBestEffort(medplum, provenance, [{ reference: organizationReference }]);
}

/**
 * Whether every Organization the saved resource is filed under is the given one.
 *
 * @param provenance The saved Provenance, carrying the accounts and compartments the server assigned.
 * @param organizationReference The practice the record belongs to, as `Organization/<id>`.
 * @returns True when that practice is the only Organization in `meta.accounts` and `meta.compartment`.
 */
function isFiledOnlyUnder(provenance: Provenance, organizationReference: string): boolean {
  const organizationReferences = [
    ...(provenance.meta?.accounts ?? []),
    ...(provenance.meta?.compartment ?? []),
  ].reduce<string[]>((references, { reference }: Reference) => {
    if (reference?.startsWith("Organization/")) references.push(reference);
    return references;
  }, []);

  return (
    organizationReferences.length > 0 &&
    organizationReferences.every((reference) => reference === organizationReference)
  );
}

/**
 * Records who changed the service after a ChargeItem write, then returns that ChargeItem.
 * A missing Provenance is a history gap. `recordChargeItemProvenance` reports it to Sentry.
 */
export async function afterChargeItemWrite(
  medplum: MedplumClient,
  written: ChargeItem,
  activityCode: ChargeItemProvenanceActivityCode,
  practiceOrganizationId?: string,
): Promise<ChargeItem> {
  await recordChargeItemProvenance(medplum, written, activityCode, practiceOrganizationId);
  return written;
}

/**
 * Records who changed each service after a multi-ChargeItem write.
 *
 * Each record can run the Set Accounts bot, so writes are capped at a few at a time and wait for the
 * rate-limit window when quota is nearly spent.
 */
export async function afterChargeItemWrites(
  medplum: MedplumClient,
  written: ChargeItem[],
  activityCode: ChargeItemProvenanceActivityCode,
  practiceOrganizationId?: string,
): Promise<ChargeItem[]> {
  return processWithConcurrencyLimit(
    written,
    async (chargeItem) => {
      await waitForRateLimitRenewalIfNeeded(medplum);
      return afterChargeItemWrite(medplum, chargeItem, activityCode, practiceOrganizationId);
    },
    CHARGE_ITEM_PROVENANCE_CONCURRENCY,
  );
}

const CHARGE_ITEM_PROVENANCE_CONCURRENCY = 3;
